import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { patientsApi } from '@/api/patients';
import { surveysApi, Survey } from '@/api/surveys';
import { testsApi, Test } from '@/api/tests';
import InstrumentPickerRow, {
    InstrumentPickerRowSkeleton,
} from '@/components/assignments/InstrumentPickerRow';
import SurveyAssignWizard from '@/components/assignments/SurveyAssignWizard';
import TestAssignWizard from '@/components/assignments/TestAssignWizard';
import { Book } from '@/components/icons';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import {
    AppDialogLayout,
    EmptyState,
    FACET_ALL_VALUE,
    FacetSelect,
    SearchField,
} from '@/components/system';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebounce } from '@/hooks/use-debounce';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { usePatientTestAssignments } from '@/hooks/use-patient-test-assignments';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import {
    buildCategoryOptions,
    filterAndSortInstruments,
    findAmbiguousAcronyms,
} from '@/lib/instrument-filters';
import { parseInstrumentName } from '@/lib/instrument-name';
import { TestStatusBadge } from '@/pages/patients/components/StatusBadges';
import { Patient } from '@/types/patient';
import { toTitleCase } from '@/utils/toTitleCase';
import type { PatientTestAssignmentState, PatientTestStatus } from '@/types/patient-overview';

interface PatientAssignModalProps {
    patient: Patient;
    mode: 'tests' | 'surveys';
    fromReport?: boolean;
}

type TestAssignmentSummary = {
    status: PatientTestStatus;
    assignmentState: PatientTestAssignmentState;
};

/** Copy carried by the report wizard, where the send has AI implications. */
const FROM_REPORT_DESCRIPTION =
    'Selecione os instrumentos para envio. O paciente pode responder normalmente, e a consolidação automática dependerá de créditos de IA disponíveis no momento da geração.';

const renderSurveySkeletonRows = (count: number) =>
    Array.from({ length: count }).map((_, index) => (
        <div
            key={`skeleton-${index}`}
            className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
        >
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-8 w-24" />
        </div>
    ));

export default function PatientAssignModal({
    patient,
    mode,
    fromReport = false,
}: PatientAssignModalProps) {
    const [activeTest, setActiveTest] = useState<Test | null>(null);
    const [activeSurvey, setActiveSurvey] = useState<Survey | null>(null);
    const [step, setStep] = useState<'list' | 'assign-test' | 'assign-survey'>('list');
    const [assignedTestStatuses, setAssignedTestStatuses] = useState<
        Record<string, TestAssignmentSummary>
    >({});
    const [assignedSurveyIds, setAssignedSurveyIds] = useState<string[]>([]);
    // Search and category live here rather than in the list below, so stepping
    // into the wizard and back does not silently reset what the user filtered.
    const [searchQuery, setSearchQuery] = useState('');
    const [category, setCategory] = useState(FACET_ALL_VALUE);
    const debouncedQuery = useDebounce(searchQuery, 250);
    const sendTestAccess = useFeatureEntitlement('tests.send');
    const sendSurveyAccess = useFeatureEntitlement('surveys.send');

    const isTestsMode = mode === 'tests';
    const shouldLoadTests = isTestsMode;
    const shouldLoadSurveys = !isTestsMode;

    const { data: overview } = useQuery({
        queryKey: ['patients', patient.id, 'overview'],
        queryFn: () => patientsApi.getOverview(patient.id),
        enabled: !!patient?.id && shouldLoadSurveys,
    });

    const { data: assignedTests = [] } = usePatientTestAssignments(
        shouldLoadTests ? patient.id : undefined,
    );

    const { data: tests = [], isLoading: isLoadingTests } = useQuery<Test[]>({
        queryKey: ['tests', 'list'],
        queryFn: testsApi.list,
        enabled: shouldLoadTests,
        initialData: [],
    });

    const { data: surveys = [], isLoading: isLoadingSurveys } = useQuery<Survey[]>({
        queryKey: ['surveys'],
        queryFn: surveysApi.list,
        enabled: shouldLoadSurveys,
        initialData: [],
    });

    const assignmentSummariesByItemId = useMemo(() => {
        const latestTestAssignments = new Map<string, TestAssignmentSummary>();

        assignedTests.forEach((test) => {
            if (!latestTestAssignments.has(test.test_id)) {
                latestTestAssignments.set(test.test_id, {
                    status: test.status,
                    assignmentState: test.assignment_state,
                });
            }
        });

        Object.entries(assignedTestStatuses).forEach(([testId, assignment]) => {
            latestTestAssignments.set(testId, assignment);
        });

        return {
            tests: latestTestAssignments,
            surveys: new Set(overview?.surveys?.map((survey) => survey.survey_id) || []),
        };
    }, [assignedTestStatuses, assignedTests, overview?.surveys]);

    const categoryOptions = useMemo(() => buildCategoryOptions(tests), [tests]);
    const ambiguousAcronyms = useMemo(() => findAmbiguousAcronyms(tests), [tests]);
    const filteredTests = useMemo(
        () => filterAndSortInstruments(tests, { query: debouncedQuery, category }),
        [tests, debouncedQuery, category],
    );

    const normalizedSurveyQuery = debouncedQuery.trim().toLowerCase();
    const filteredSurveys = useMemo(
        () =>
            normalizedSurveyQuery
                ? surveys.filter((survey) =>
                      survey.name.toLowerCase().includes(normalizedSurveyQuery),
                  )
                : surveys,
        [surveys, normalizedSurveyQuery],
    );

    const items = isTestsMode ? tests : surveys;
    const filteredItems = isTestsMode ? filteredTests : filteredSurveys;
    const hasActiveFilters = searchQuery.trim() !== '' || category !== FACET_ALL_VALUE;
    const isLoadingItems = isTestsMode ? isLoadingTests : isLoadingSurveys;

    const handleClearFilters = () => {
        setSearchQuery('');
        setCategory(FACET_ALL_VALUE);
    };

    const handleAssignTest = (test: Test) => {
        if (sendTestAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendTestAccess.decision));
            return;
        }

        setActiveSurvey(null);
        setActiveTest(test);
        setStep('assign-test');
    };

    const handleAssignSurvey = (survey: Survey) => {
        if (sendSurveyAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendSurveyAccess.decision));
            return;
        }

        setActiveTest(null);
        setActiveSurvey(survey);
        setStep('assign-survey');
    };

    const handleExitWizard = () => {
        setActiveTest(null);
        setActiveSurvey(null);
        setStep('list');
    };

    const handleAssignedTest = (testId: string) => {
        setAssignedTestStatuses((prev) => ({
            ...prev,
            [testId]: {
                status: 'not_started',
                assignmentState: 'active',
            },
        }));
    };

    const handleAssignedSurvey = (surveyId: string) => {
        setAssignedSurveyIds((prev) => (prev.includes(surveyId) ? prev : [...prev, surveyId]));
    };

    if (step === 'assign-test' && activeTest) {
        return (
            <TestAssignWizard
                test={activeTest}
                preSelectedPatient={patient}
                onClose={handleExitWizard}
                allowPatientSelection={false}
                onAssigned={handleAssignedTest}
                fromReport={fromReport}
            />
        );
    }

    if (step === 'assign-survey' && activeSurvey) {
        return (
            <SurveyAssignWizard
                survey={activeSurvey}
                preSelectedPatient={patient}
                onClose={handleExitWizard}
                allowPatientSelection={false}
                onAssigned={handleAssignedSurvey}
            />
        );
    }

    /**
     * Badge and action for an instrument. An active assignment yields a disabled
     * "Enviado" — one word for the fact that a send exists, while the badge
     * carries how far the patient got ("Não iniciado", "Em andamento"). The two
     * no longer say the same thing, which is what the earlier disabled label
     * ("Em andamento" in both places) did. Overdue and completed stay
     * actionable, since reassigning there is a real action.
     */
    const getTestAssignmentMeta = (testId: string) => {
        const latestAssignment = assignmentSummariesByItemId.tests.get(testId);

        if (!latestAssignment) {
            return { badge: null, buttonLabel: 'Enviar', isActionDisabled: false };
        }

        const badge = (
            <TestStatusBadge
                status={latestAssignment.status}
                assignmentState={latestAssignment.assignmentState}
            />
        );

        if (
            latestAssignment.assignmentState === 'overdue' ||
            latestAssignment.assignmentState === 'completed'
        ) {
            return { badge, buttonLabel: 'Reenviar', isActionDisabled: false };
        }

        return { badge, buttonLabel: 'Enviado', isActionDisabled: true };
    };

    const renderTestRows = () =>
        filteredTests.map((test) => {
            const { badge, buttonLabel, isActionDisabled } = getTestAssignmentMeta(test.id);

            return (
                <InstrumentPickerRow
                    key={test.id}
                    test={test}
                    query={debouncedQuery}
                    showVariant={ambiguousAcronyms.has(
                        parseInstrumentName(test.name, test.test_id).acronym,
                    )}
                    badge={badge}
                    action={
                        isActionDisabled ? (
                            <Button size="sm" variant="surface" className="shrink-0" disabled>
                                {buttonLabel}
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                variant="tint"
                                className="shrink-0"
                                onClick={() => handleAssignTest(test)}
                                aria-disabled={sendTestAccess.shouldBlock}
                            >
                                {buttonLabel}
                                {sendTestAccess.isUnavailableInPlan &&
                                sendTestAccess.availableFromPlan ? (
                                    <FeatureUnavailableChip
                                        availablePlan={sendTestAccess.availableFromPlan}
                                    />
                                ) : null}
                            </Button>
                        )
                    }
                />
            );
        });

    const renderSurveyRows = () =>
        filteredSurveys.map((survey) => {
            const isAssigned =
                assignmentSummariesByItemId.surveys.has(survey.id) ||
                assignedSurveyIds.includes(survey.id);

            return (
                <div
                    key={survey.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card py-2 pl-3.5 pr-2.5 transition-colors hover:border-border-strong"
                >
                    <p className="min-w-0 flex-1 text-pretty text-sm font-medium text-foreground">
                        {toTitleCase(survey.name)}
                    </p>
                    {isAssigned ? (
                        <Button size="sm" variant="surface" className="shrink-0" disabled>
                            Enviado
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            variant="tint"
                            className="shrink-0"
                            onClick={() => handleAssignSurvey(survey)}
                            aria-disabled={sendSurveyAccess.shouldBlock}
                        >
                            Enviar
                            {sendSurveyAccess.isUnavailableInPlan &&
                            sendSurveyAccess.availableFromPlan ? (
                                <FeatureUnavailableChip
                                    availablePlan={sendSurveyAccess.availableFromPlan}
                                />
                            ) : null}
                        </Button>
                    )}
                </div>
            );
        });

    return (
        <AppDialogLayout
            title={isTestsMode ? 'Enviar instrumento ao paciente' : 'Enviar formulário ao paciente'}
            description={fromReport ? FROM_REPORT_DESCRIPTION : undefined}
            bodyClassName="flex min-h-0 flex-col p-0"
        >
            {/* Toolbar and list scroll independently: with forty instruments the
                search has to stay put while the results move under it. */}
            <div className="shrink-0 border-b border-border/70 px-6 pb-3">
                <div className="flex items-center gap-2">
                    <SearchField
                        className="min-w-0 flex-1"
                        placeholder={
                            isTestsMode
                                ? 'Buscar por nome, sigla ou tema...'
                                : 'Buscar formulário...'
                        }
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                    />
                    {isTestsMode && categoryOptions.length > 0 ? (
                        <FacetSelect
                            label="Categoria"
                            options={categoryOptions}
                            totalCount={tests.length}
                            value={category}
                            onChange={setCategory}
                            searchPlaceholder="Filtrar categoria..."
                        />
                    ) : null}
                </div>
                {isLoadingItems ? null : (
                    <p className="mt-2.5 text-xs text-muted-foreground" aria-live="polite">
                        {hasActiveFilters
                            ? `${filteredItems.length} de ${items.length}`
                            : `${items.length} disponíveis`}
                    </p>
                )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-6 py-3">
                {isLoadingItems ? (
                    isTestsMode ? (
                        Array.from({ length: 5 }).map((_, index) => (
                            <InstrumentPickerRowSkeleton key={`skeleton-${index}`} />
                        ))
                    ) : (
                        renderSurveySkeletonRows(3)
                    )
                ) : filteredItems.length === 0 ? (
                    <EmptyState
                        visual="icon"
                        icon={Book}
                        className="border-0 bg-transparent px-0 py-8 shadow-none"
                        title={
                            isTestsMode
                                ? 'Nenhum instrumento encontrado'
                                : 'Nenhum formulário encontrado'
                        }
                        description={
                            hasActiveFilters
                                ? 'Ajuste sua busca ou o filtro de categoria para localizar o que procura.'
                                : 'Nenhum item disponível no momento.'
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" onClick={handleClearFilters}>
                                    Limpar filtros
                                </Button>
                            ) : undefined
                        }
                    />
                ) : isTestsMode ? (
                    renderTestRows()
                ) : (
                    renderSurveyRows()
                )}
            </div>
        </AppDialogLayout>
    );
}
